/**
 * WHAT:
 *   Error display component with PDF opening and navigation.
 *
 * WHY:
 *   Centralizes error display UI for better maintainability.
 *
 * DOMAIN: Investments → Parqet → Test Tab → Components
 *
 * DEPENDENCIES:
 *   - TypeScript types (ErrorDisplayProps)
 *
 * RISKS:
 *   - NOTE: PDF opening may fail if file path is invalid
 *   - NOTE: Navigation may fail if index is out of bounds
 *   - NOTE: Error messages may be truncated if too long
 */

import type { ErrorDisplayProperties, ErrorPdf } from "../types";

/**
 * WHAT:
 *   Component displaying error messages with optional PDF opening and navigation.
 *
 * WHY:
 *   Encapsulates error display UI logic.
 *
 * @param root0
 *
 * @param root0.error
 *
 * @param root0.parsedData
 *
 * @param root0.allErrorPdfs
 *
 * @param root0.currentErrorIndex
 *
 * @param root0.onOpenPdf
 *
 * @param root0.onPreviousError
 *
 * @param root0.onNextError
 *
 * @returns {JSX.Element | null} The ErrorDisplay component or null if no error
 *
 */
export const ErrorDisplay = ({
  allErrorPdfs,
  currentErrorIndex,
  error,
  onNextError,
  onOpenPdf,
  onPreviousError,
  parsedData,
}: ErrorDisplayProperties) => {
  if (!error) {
    return null;
  }

  // Reason: Type assertion needed for parsedData.filePath access
  const parsedDataTyped = parsedData as undefined | { filePath?: string };
  const filePath: string | undefined = parsedDataTyped?.filePath;
  // Reason: Explicit type for props to avoid unsafe assignment
  const errorPdfsArray: readonly ErrorPdf[] = allErrorPdfs as readonly ErrorPdf[];
  const hasErrorPdfs: boolean = errorPdfsArray.length > 0;
  const errorPdfCount: number = errorPdfsArray.length;
  const currentIndex: number = currentErrorIndex;

  // Reason: Explicit types for callbacks to avoid unsafe call
  const handleOpenPdf = (path: string): void => {
    (onOpenPdf as (filePath: string) => void)(path);
  };
  const handlePreviousError = (): void => {
    (onPreviousError as () => void)();
  };
  const handleNextError = (): void => {
    (onNextError as () => void)();
  };

  return (
    <div className="mt-4 text-red-400 bg-red-500/10 p-3 rounded-lg border border-red-500/30">
      {error}
      {filePath || hasErrorPdfs ? (
        <div className="mt-3 flex items-center gap-3">
          {filePath ? (
            <button
              className="px-3 py-1 bg-blue-600/20 text-blue-400 rounded text-sm hover:bg-blue-600/30 transition"
              onClick={() => {
                if (filePath) {
                  handleOpenPdf(filePath);
                }
              }}
            >
              📄 PDF öffnen
            </button>
          ) : null}
          {errorPdfCount > 1 && (
            <div className="flex items-center gap-2">
              <button onClick={handlePreviousError}>← Vorherige</button>
              <span className="text-xs text-slate-400">
                {String(currentIndex + 1)} / {String(errorPdfCount)}
              </span>
              <button onClick={handleNextError}>Nächste →</button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};
